import { useAdminGet } from '@reapitform/hooks'
import { EnumCatalog } from '../types'

export const ADMIN_ENUMS_QUERY_KEY = ['admin-enums']

/**
 * Fetches the enum option catalog (keyed by enum name, e.g. `FormType`, `Region`, `Country`) used to
 * populate the builder's dropdowns and to translate stored enum values into display labels.
 */
export const useAdminEnums = () => {
  const [data, loading, errorString] = useAdminGet<EnumCatalog>({
    path: '/api/enums',
    retry: false,
    queryKeys: ADMIN_ENUMS_QUERY_KEY,
  })

  return { data, loading, errorString }
}

/**
 * Resolves the display label for an enum value; falls back to the raw value when it is unknown. The
 * `NONE` sentinel (e.g. an unset Region/Country) renders as an empty string.
 */
export const getEnumLabel = (catalog: EnumCatalog | null, enumName: string, value?: string | null): string => {
  if (!value || value === 'None') return ''
  const option = catalog?.[enumName]?.find((item) => item.value === value)
  return option?.label ?? value
}

/**
 * Resolves the short-form label for an enum value (e.g. "SA" for Region, "AU" for Country) — used where
 * space is tight, like disambiguating same-named forms by region/country in a picker.
 */
export const getEnumShortName = (catalog: EnumCatalog | null, enumName: string, value?: string | null): string => {
  if (!value || value === 'None') return ''
  const option = catalog?.[enumName]?.find((item) => item.value === value)
  return option?.shortName || option?.label || value
}

/**
 * Resolves the document family ("Agreement" | "Notice") a `FormType` value belongs to, from the same
 * per-option `family` the catalog already carries — so this stays in sync with the API's
 * `FormFamilyAttribute` automatically as sub-types are added, with no separate mapping to maintain here.
 * Returns `''` for an unset/unknown FormType (e.g. before the catalog has loaded).
 */
export const getFormFamily = (catalog: EnumCatalog | null, formType?: string | null): string => {
  if (!formType || formType === 'None') return ''
  return catalog?.FormType?.find((item) => item.value === formType)?.family || ''
}
